import { Capacitor } from "@capacitor/core";

import { oneSignalAppId } from "../env";

/**
 * Outcome of asking for push-notification permission.
 *
 * - `granted`     — permission is (now) authorized; the user was associated with OneSignal.
 * - `denied`      — the user just declined the native system dialog. Block advancing and warn.
 * - `blocked`     — permission was declined on a previous run, so iOS/Android won't show the
 *                   dialog again. We deep-link to the app's Settings instead. Block advancing.
 * - `unsupported` — web (or no App ID configured); nothing to do, advance gracefully.
 */
export type NotificationPermissionResult = "granted" | "denied" | "blocked" | "unsupported";

/** Map OneSignal read APIs to the shared client status model (no system prompt). */
export function mapOneSignalPermissionState(
  hasPermission: boolean,
  canRequestPermission: boolean,
): NotificationPermissionResult {
  if (hasPermission) return "granted";
  if (!canRequestPermission) return "blocked";
  return "denied";
}

// OneSignal must be initialized exactly once per app session.
let initialized = false;

type OneSignalPlugin = typeof import("@onesignal/capacitor-plugin").default;

async function getOneSignal(): Promise<OneSignalPlugin | null> {
  if (Capacitor.getPlatform() === "web") return null;
  if (!oneSignalAppId) {
    console.warn("VITE_ONESIGNAL_APP_ID is not set; skipping OneSignal push setup.");
    return null;
  }

  const OneSignal = (await import("@onesignal/capacitor-plugin")).default;
  if (!initialized) {
    OneSignal.initialize(oneSignalAppId);
    initialized = true;
  }
  return OneSignal;
}

/**
 * Read current push-notification permission without presenting the OS dialog.
 * Web (or missing App ID) returns `unsupported`.
 */
export async function getNotificationPermissionStatus(): Promise<NotificationPermissionResult> {
  const OneSignal = await getOneSignal();
  if (!OneSignal) return "unsupported";

  const hasPermission = await OneSignal.Notifications.hasPermission();
  const canRequestPermission = await OneSignal.Notifications.canRequestPermission();
  return mapOneSignalPermissionState(hasPermission, canRequestPermission);
}

/**
 * Requests push-notification permission via OneSignal (native only) and, on grant,
 * associates the device with OneSignal so it can be targeted later.
 *
 * iOS only presents the system permission dialog once. We therefore inspect the
 * permission state before prompting:
 *   - not yet asked  → show the native dialog.
 *   - already denied → the OS won't prompt again, so fall back to opening Settings.
 *
 * @param externalId Stable user identity (the canonical device id) used for `OneSignal.login`.
 * @see https://documentation.onesignal.com/docs/ionic-capacitor-cordova-sdk-setup
 */
export async function requestNotificationPermission(
  externalId?: string,
): Promise<NotificationPermissionResult> {
  const OneSignal = await getOneSignal();
  if (!OneSignal) return "unsupported";

  // Already authorized (e.g. enabled in Settings on a previous run).
  if (await OneSignal.Notifications.hasPermission()) {
    await associateUser(OneSignal, externalId);
    return "granted";
  }

  // `canRequestPermission` is true only while the OS will still show the dialog
  // (iOS NotDetermined / Android never-asked). Once denied it returns false.
  if (!(await OneSignal.Notifications.canRequestPermission())) {
    // Subsequent tap after a denial: the system dialog won't appear again, so
    // surface the app's notification Settings via OneSignal's settings fallback.
    // If the user enables notifications there and returns, this resolves true.
    const enabledViaSettings = await OneSignal.Notifications.requestPermission(true);
    if (enabledViaSettings) {
      await associateUser(OneSignal, externalId);
      return "granted";
    }
    return "blocked";
  }

  const granted = await OneSignal.Notifications.requestPermission(false);
  if (granted) {
    await associateUser(OneSignal, externalId);
    return "granted";
  }
  return "denied";
}

async function associateUser(OneSignal: OneSignalPlugin, externalId?: string): Promise<void> {
  if (!externalId) return;
  try {
    await OneSignal.login(externalId);
  } catch (error) {
    // Subscription still exists anonymously; association can be retried on next launch.
    console.warn("OneSignal.login failed", error);
  }
}
